import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Material, MeshComponent, NumberArray, Object3D, } from '@wonderlandengine/api';
import { CaliperOptions, WheelTypes } from '../data/configuration.js';
import { WheelDescription } from './components/wheel-description.js';
import { Utilities } from '../utilities/utilities.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TweenManager } from '../utilities/tween-manager.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface StandardMaterial extends Material {
	albedoColor: NumberArray;
	metallicFactor: number;
	smoothnessFactor: number;
}

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const COLOUR_TWEEN_TIME_IN_MS = 250;

/**
 * Wheel Configurator:
 */
export class WheelConfigurator extends Component {
	static TypeName = 'wheel-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	wheelDescriptions!: Object3D;

	@property.object({ required: true })
	superliteWheels!: Object3D;

	@property.object({ required: true })
	superliteWheel!: Object3D;
	
	@property.object({ required: true })
	multispokeWheels!: Object3D;

	@property.object({ required: true })
	multispokeWheel!: Object3D;

	@property.object({ required: true })
	aeroliteWheels!: Object3D;

	@property.object({ required: true })
	aeroliteWheel!: Object3D;

	@property.object({ required: true })
	sportliteWheels!: Object3D;

	@property.object({ required: true })
	sportliteWheel!: Object3D;
	
	@property.object({ required: true })
	sportliteFrontCalipers!: Object3D;

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _wheelDescriptions!: WheelDescription[];
	private _superliteWheel!: MeshComponent;
	private _multispokeWheel!: MeshComponent;
	private _aeroliteWheel!: MeshComponent;
	private _sportliteWheel!: MeshComponent;
	private _sportliteFrontCalipers!: MeshComponent;
	private _lastWheelType: WheelTypes | null = null;

	private _tweenManager = new TweenManager();

	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start() {

		// get components.
		this._wheelDescriptions = this.wheelDescriptions.getComponents(WheelDescription)!;
		this._superliteWheel = this.superliteWheel.getComponent(MeshComponent)!;
		this._multispokeWheel = this.multispokeWheel.getComponent(MeshComponent)!;
		this._aeroliteWheel = this.aeroliteWheel.getComponent(MeshComponent)!;
		this._sportliteWheel = this.sportliteWheel.getComponent(MeshComponent)!;
		this._sportliteFrontCalipers = this.sportliteFrontCalipers.getComponent(MeshComponent)!;
	}

	update() {
		this._tweenManager.update();
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/**
	 * Selects a front brake caliper with the given ID, updating the relevant meshes/materials on the vehicle model.
	  */
	selectFrontBrakeCaliper(id: string) {

		// get the current caliper material and ensure it is valid.
		const material = this._sportliteFrontCalipers.material?.clone() as StandardMaterial;
		if (!material) {
			console.error('No caliper material assigned');
			return;
		}

		// set the material colour based on the selected caliper ID.
		// the albedo texture itself defines the actual colour, so we set the albedo colour to white for the Coral caliper and black for the standard option.
		material.albedoColor = (id === CaliperOptions.Coral) ? [1, 1, 1, 0] : [0, 0, 0, 0];

		// fade between the colours.
		this._fadeColour(this._sportliteFrontCalipers, material);
	}

	/**
	 * Selects a wheel style with the given ID, updating the relevant meshes/materials on the vehicle model.
	 */
	selectWheelStyle(id: string) {

		// get the wheel description for the given ID.
		const wheelDescription = this._wheelDescriptions.find(element => element.wheelId === id);
		if (!wheelDescription) {
			console.error('No wheel description found for id: ', id);
			return;
		}
		
		// get the relevant material.
		const material = wheelDescription.paintMaterial as StandardMaterial;
		if (!material) {
			console.error('No paint material assigned for id: ', id);
			return;
		}
		
		// get the wheel type from the description.
		const wheelType = wheelDescription.wheelType as WheelTypes;

		// get the relevant mesh components.
		let wheelMesh: MeshComponent;
		switch (wheelType) {
			case WheelTypes.Multispoke:
				wheelMesh = this._multispokeWheel;
				break;
			case WheelTypes.Aerolite:
				wheelMesh = this._aeroliteWheel;
				break;
			default:
			case WheelTypes.Superlite:
				wheelMesh = this._superliteWheel;
				break;
			case WheelTypes.Sportlite:
				wheelMesh = this._sportliteWheel;
				break;
		}
		
		// if the wheel type has changed (or on first call)...
		if (wheelType !== this._lastWheelType) {

			// activate the relevant objects.
			Utilities.setActiveAll(this.superliteWheels, wheelType === WheelTypes.Superlite);
			Utilities.setActiveAll(this.multispokeWheels, wheelType === WheelTypes.Multispoke);
			Utilities.setActiveAll(this.aeroliteWheels, wheelType === WheelTypes.Aerolite);
			Utilities.setActiveAll(this.sportliteWheels, wheelType === WheelTypes.Sportlite);

			// set the materials.
			wheelMesh.material = material;
		}

		// otherwise, smoothly fade between the current material and the selected material.
		else {
			this._fadeColour(wheelMesh, material);
		}

		// update the flag.
		this._lastWheelType = wheelType;
	}

	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------

	/**
	 * Smoothly fades the colour of the material on the given mesh between the current colour and the colour defined by the given material,
	 * using a tween animation to change the relevant material properties.
	 */
	private _fadeColour(mesh: MeshComponent, targetMaterial: StandardMaterial) {

		// ensure the given mesh has a material assigned to it.
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		// clone the current material (as we don't want to change the source material itself).
		const material = mesh.material.clone() as StandardMaterial;
		mesh.material = material;
		
		// tween the relevant material properties to change the colour.
		const colourTween = new Tween({
			albedoColor: material.albedoColor,
			metallicFactor: material.metallicFactor,
			smoothnessFactor: material.smoothnessFactor
		});
		colourTween
			.to(
				{
					albedoColor: targetMaterial.albedoColor,
					metallicFactor: targetMaterial.metallicFactor,
					smoothnessFactor: targetMaterial.smoothnessFactor
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				material.albedoColor = c.albedoColor;
				material.metallicFactor = c.metallicFactor;
				material.smoothnessFactor = c.smoothnessFactor;
			})
			.easing(Easing.Quadratic.InOut);
		this._tweenManager.start(colourTween);
	}
}
